/**
 * Build the rows for a scope-grouped operating-config table.
 *
 * Rows = one "Organization default" row per distinct district found on the
 * user's sites, each followed by that district's site rows (sorted by name).
 * The common single-district case yields `[Organization default, …sites]`.
 *
 * Districts are derived from the sites themselves (`site.district` /
 * `site.district_name`) rather than `useActiveOrganization` (whose id is
 * school-shaped in places). Each row is decorated `hasOwn` from a per-surface
 * set of scope keys so the table can badge Inherited vs Override/Configured.
 */

import { useMemo } from 'react';
import { useSites } from '../../hooks/queries';
import type { ScopeRow } from './types';
import { orgKey, siteKey } from './types';

export interface ScopeRowsResult {
  rows: ScopeRow[];
  isLoading: boolean;
  error: unknown;
}

const DEFAULT_DISTRICT_NAME = 'Organization';

export function useScopeRows(ownKeys: Set<string>): ScopeRowsResult {
  const { data, isLoading, error } = useSites({ page_size: 100 });

  const rows = useMemo<ScopeRow[]>(() => {
    const sites = data?.results ?? [];

    // Distinct districts (skip sites with no district — they still get a
    // site row, just no org-tier parent).
    const districtNames = new Map<number, string>();
    for (const s of sites) {
      if (s.district != null && !districtNames.has(s.district)) {
        districtNames.set(s.district, s.district_name ?? DEFAULT_DISTRICT_NAME);
      }
    }

    const sitesByDistrict = new Map<number | null, typeof sites>();
    for (const s of sites) {
      const list = sitesByDistrict.get(s.district) ?? [];
      list.push(s);
      sitesByDistrict.set(s.district, list);
    }

    const sortByName = <T extends { name: string }>(arr: T[]) =>
      [...arr].sort((a, b) => a.name.localeCompare(b.name));

    const out: ScopeRow[] = [];
    const districtEntries = [...districtNames.entries()].sort((a, b) =>
      a[1].localeCompare(b[1]),
    );

    for (const [districtId, districtName] of districtEntries) {
      out.push({
        key: orgKey(districtId),
        kind: 'org',
        districtId,
        districtName,
        schoolId: null,
        schoolName: null,
        hasOwn: ownKeys.has(orgKey(districtId)),
      });
      for (const s of sortByName(sitesByDistrict.get(districtId) ?? [])) {
        out.push({
          key: siteKey(s.id),
          kind: 'site',
          districtId,
          districtName,
          schoolId: s.id,
          schoolName: s.name,
          hasOwn: ownKeys.has(siteKey(s.id)),
        });
      }
    }

    // Sites with no district (rare) — emit as site rows with no org parent.
    for (const s of sortByName(sitesByDistrict.get(null) ?? [])) {
      out.push({
        key: siteKey(s.id),
        kind: 'site',
        districtId: 0,
        districtName: DEFAULT_DISTRICT_NAME,
        schoolId: s.id,
        schoolName: s.name,
        hasOwn: ownKeys.has(siteKey(s.id)),
      });
    }

    return out;
  }, [data, ownKeys]);

  return { rows, isLoading, error };
}
